import React, { useEffect, useRef, useState } from 'react';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { useRotationStore } from 'src/context/RotationContext';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import StowageStatusIcon from 'src/components/ui/status-icon/StowageStatusIcon';
import { StowageStatusEnum } from 'src/enums/stowage/stowageStatusEnum';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useStowageStore } from 'src/context/StowageContext';
import { BurgerInfoMenu } from '../Stowage.styles';
import { TreeNode } from 'src/types/ViewModel/Stowage/TreeNode';
import { calculateRotationCallStatuses, getRotationCallStatusInPortCall } from 'src/utils/stowage/stowageOperation/rotationCallStatusesUtils';
import StowageRotationTreeBerthCallItemComponent from './StowageRotationTreeBerthCallItem';
import ViolationListIconComponent from '../violationListView/ViolationListIcon';
import { useViolationsStore } from 'src/context/ViolationsContext';
import { StowageRuleConsequenceType } from 'src/enums/stowage/stowageRule/stowageRuleConsequenceType';
import { getPortCallViolationStatus } from 'src/utils/stowage/violations/violationsUtils';
import { PortCallStatusEnum } from 'src/enums/stowage/portCallStatusEnum';
import { UUID } from 'crypto';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';
import { useRotationKeyboard } from 'src/hooks/stowage/keyboardHooks/useStowageRotationKeyboard';
import {
    onBerthCallDragEnd,
    onBerthCallDragOver,
    onBerthCallDragStart,
    onDragoverBerthCallParent,
} from 'src/utils/stowage/dragAndDrop/dragAndDropBerthCallUtils';
import { stowageFeatureEnum } from 'src/enums/stowage/stowageFeatureEnum';
import { hasPermission } from 'src/utils/stowage/permissions/stowagePermissionsUtils';
import { useAuthStore } from 'src/context/AuthContext';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import { useVesselStore } from 'src/context/VesselContext';
import { onBerthCallDrop } from 'src/utils/stowage/dragAndDrop/dragAndDropCargoOperationUtils';
import { getBerthCalls, isManualCall } from 'src/utils/stowage/stowageRotationUtils';
import { deepCopy } from 'src/utils/objectUtils';
import SequenceChangedIcon from 'src/components/ui/status-icon/SequenceChangedIcon';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';

type Position = {
    x: number;
    y: number;
};

type Props = {
    portCall: RotationPortCall;
    filteredStowageRotationTree: RotationPortCall[];
    openPortCallMenuItemGuids: UUID[];
    setOpenPortCallMenuItemGuids: (guids: UUID[]) => void;
};

const StowageRotationTreePortCallItemComponent: React.FC<Props> = (props) => {
    const { selectedStowageRotationPortCall, selectedStowageRotationBerthCall, stowageRotationTree } = useRotationStore();
    const { rotationMenuOpen, setRotationMenuOpen, activeStowagePlan, rotationCallStatuses, setRotationCallStatuses, manifest } = useStowageStore();
    const [showRotationListMenuItem, setShowRotationListMenuItem] = useState<TreeNode | null>(null);
    const { setQueryParamStowagePortCall } = useStowageQueryParamsUtils();
    const { setSelectedTab, setSelectedViolationListFilter, setSelectedCargoOperation, setSelectedTanks } = useStowageSelectionStore();
    const { violations } = useViolationsStore();
    const { role } = useAuthStore();
    const { vessel } = useVesselStore();
    const [violationType, setViolationType] = useState<StowageRuleConsequenceType | null>(null);
    const [rotationItemPos, setRotationItemPos] = useState<Position | null>(null);
    const { saveStowageData } = usePostStowageApi();
    const { handleRotationArrowClicks } = useRotationKeyboard();
    const draggoverElement = useRef<HTMLDivElement>(null);
    const dragoverBerthCall = useRef<RotationBerthCall>(null);
    const dragStartBerthCall = useRef<RotationBerthCall>(null);
    useEffect(() => {
        if (!stowageRotationTree || !activeStowagePlan || !manifest) return;
        const _rotationCallStatuses = calculateRotationCallStatuses(activeStowagePlan, vessel.firstVoyageOrcaWebStowageImosSync);
        setRotationCallStatuses(_rotationCallStatuses);
    }, [stowageRotationTree, activeStowagePlan, manifest]);

    useEffect(() => {
        if (violations) {
            setViolationType(getPortCallViolationStatus(props.portCall.portCallGuid, violations));
        }
    }, [violations]);

    useEffect(() => {
        if (!selectedStowageRotationPortCall) return;
        openPortCalls(selectedStowageRotationPortCall);
    }, [selectedStowageRotationPortCall]);

    const getSelectedPortCallClass = () => {
        if (
            selectedStowageRotationPortCall &&
            !selectedStowageRotationBerthCall &&
            selectedStowageRotationPortCall.portCallGuid === props.portCall.portCallGuid
        ) {
            return 'is-selected-port-call';
        }
    };

    const getDepartedPortCallClass = () => {
        return props.portCall.portCallStatus === PortCallStatusEnum.Departed ? 'departed-port-call' : '';
    };

    const onShowRotationMenu = (event: any, portCall: RotationPortCall | null, bertCall: RotationBerthCall | null) => {
        event.preventDefault();
        event.stopPropagation();

        if (rotationMenuOpen && rotationMenuOpen.isVisible) {
            setRotationMenuOpen(null);
        } else {
            const yPos = rotationItemPos.y;
            const menuItem = { isVisible: true, top: yPos, portCall: portCall, berthCall: bertCall };
            setRotationMenuOpen(menuItem);
        }
    };

    const getPortNodeCssClass = (portCallItem: RotationPortCall): string => {
        let cssClasses = ' port-node ';

        if (props.openPortCallMenuItemGuids.includes(portCallItem?.portCallGuid)) {
            cssClasses += ' open-port-node ';
        }

        return cssClasses;
    };

    const getExtNodeCssClass = (): string => {
        const cssClasses = ' rotation-list-item ';

        return cssClasses;
    };

    const onMouseEnterRotationListItem = (event: any, guid: UUID, rotationNodeType: string) => {
        event.preventDefault();
        event.stopPropagation();
        const xPos = event.clientX;
        const yPos = event.clientY;
        setRotationItemPos({ x: xPos, y: yPos });
        setShowRotationListMenuItem({ guid, type: rotationNodeType });
    };

    const onMouseLeaveRotationListItem = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
        setShowRotationListMenuItem(null);
    };

    const onSelectedPortCall = (event: any, portCallItem: RotationPortCall) => {
        if (portCallItem.portCallGuid !== selectedStowageRotationPortCall?.portCallGuid) {
            setQueryParamStowagePortCall(portCallItem.portCallGuid);
        } else if (portCallItem.portCallGuid === selectedStowageRotationPortCall?.portCallGuid && selectedStowageRotationPortCall) {
            setQueryParamStowagePortCall(portCallItem.portCallGuid);

            if (!selectedStowageRotationBerthCall || selectedStowageRotationBerthCall.portCallGuid !== portCallItem.portCallGuid) {
                const isOpen = props.openPortCallMenuItemGuids.find((id) => id === portCallItem.portCallGuid);
                if (isOpen) {
                    closePortCall(portCallItem);
                } else {
                    openPortCalls(portCallItem);
                }
            }
        }

        setSelectedViolationListFilter('Selected call');
        setSelectedCargoOperation(null);
        setSelectedTanks([]);

        event.preventDefault();
        event.stopPropagation();
    };

    const closePortCall = (portCallItem: RotationPortCall) => {
        props.setOpenPortCallMenuItemGuids([...props.openPortCallMenuItemGuids.filter((id) => id !== portCallItem.portCallGuid)]);
    };

    const openPortCalls = (portCallItem: RotationPortCall) => {
        if (!props.openPortCallMenuItemGuids.includes(portCallItem.portCallGuid)) {
            props.setOpenPortCallMenuItemGuids([...props.openPortCallMenuItemGuids, portCallItem.portCallGuid]);
        }
    };

    const canDragRotationItem = () => {
        return hasPermission(role, stowageFeatureEnum.EditStowage);
    };

    const onDragEnd = (event: React.DragEvent<HTMLDivElement>, berthCallItem: RotationBerthCall) => {
        const stowagePlanChanges = onBerthCallDragEnd(
            event,
            deepCopy(berthCallItem),
            activeStowagePlan.stowagePlanGuid,
            dragStartBerthCall,
            draggoverElement,
            dragoverBerthCall
        );
        if (stowagePlanChanges.length > 0) {
            saveStowageData(activeStowagePlan, stowagePlanChanges);
        }
    };

    const handleOnDrop = (e: React.DragEvent, berthCallItem: RotationBerthCall) => {
        e.preventDefault();
        e.stopPropagation();

        const stowagePlanChanges = onBerthCallDrop(e, berthCallItem, activeStowagePlan);

        if (stowagePlanChanges.length > 0) {
            saveStowageData(activeStowagePlan, stowagePlanChanges);
        }
    };

    const onViewViolationsForSelectedCall = () => {
        setSelectedTab('Violation List');
        setSelectedViolationListFilter('Selected call');
    };

    const getPortCallStatusIcon = (portCall: RotationPortCall, activeStowagePlan: StowagePlan, rotationCallStatuses): any => {
        if (!activeStowagePlan) return;
        const status = getRotationCallStatusInPortCall(rotationCallStatuses, portCall.portCallGuid);
        if (status?.numberOfCargoOperationsWithMissingStowage === 0 && status?.numberOfCargoOperations > 0) {
            return StowageStatusIcon(StowageStatusEnum.Stowed, null);
        } else if (status?.numberOfCargoOperationsWithMissingStowage > 0 && status?.numberOfCargoOperations > 0) {
            return StowageStatusIcon(StowageStatusEnum.MissingStowage, status.numberOfCargoOperationsWithMissingStowage);
        } else if (status?.numberOfCargoOperations === 0) {
            return StowageStatusIcon(StowageStatusEnum.NotApplicable, null);
        }
    };

    return (
        <li
            onClick={(e) => onSelectedPortCall(e, props.portCall)}
            onContextMenu={(e) => onShowRotationMenu(e, props.portCall, null)} // Added right-click event listener
            onKeyDown={(e) =>
                handleRotationArrowClicks(e, props.filteredStowageRotationTree, props.openPortCallMenuItemGuids, props.setOpenPortCallMenuItemGuids)
            }
            tabIndex={0}
            className={getPortNodeCssClass(props.portCall)}
            key={`p-` + props.portCall.portCallGuid + `-` + props.portCall.voyageNumber}
        >
            <div className={getExtNodeCssClass()}>
                <div
                    onMouseEnter={(e) => onMouseEnterRotationListItem(e, props.portCall.portCallGuid, 'PortCall')}
                    onMouseLeave={(e) => onMouseLeaveRotationListItem(e)}
                    className={'rotation-tree-item port-call-item ' + getSelectedPortCallClass()}
                >
                    <span className={'port-name text-overflow' + ' ' + getDepartedPortCallClass()}>
                        <span className="voyage-nr">{props.portCall.voyageNumber}</span>
                        {props.portCall.turnaroundVoyageNumber ? <span className="voyage-nr"> / {props.portCall.turnaroundVoyageNumber}</span> : null}
                        <span className="sep">-</span>
                        <span>{props.portCall.portDisplayName}</span>
                    </span>
                    <span className="icon-wrapper">
                        {isManualCall(props.portCall) && (
                            <span title="Manual call" className="manual-node-marker">
                                M
                            </span>
                        )}
                        <SequenceChangedIcon hasChangedSequence={props.portCall.hasChangedSequence} />

                        <span onClick={onViewViolationsForSelectedCall}>
                            <ViolationListIconComponent violationType={violationType} includeText={false} />
                        </span>
                        <span className="status-icon">{getPortCallStatusIcon(props.portCall, activeStowagePlan, rotationCallStatuses)}</span>
                        <BurgerInfoMenu
                            onClick={(e) => onShowRotationMenu(e, props.portCall, null)}
                            className={
                                showRotationListMenuItem?.type === 'PortCall' && showRotationListMenuItem?.guid === props.portCall.portCallGuid
                                    ? 'show-menu'
                                    : ''
                            }
                        >
                            <FontAwesomeIcon icon="ellipsis-h" />
                        </BurgerInfoMenu>
                    </span>
                </div>
                <ul className="berth-call-list">
                    {getBerthCalls(props.portCall).map((berthCallItem) => {
                        return (
                            <li
                                key={`berth-call-item-${berthCallItem.berthCallGuid}`}
                                onDragOver={(e) => onDragoverBerthCallParent(e, berthCallItem, dragStartBerthCall)}
                            >
                                <div
                                    className="dragging-parent-berth"
                                    draggable={canDragRotationItem()}
                                    onDragStart={(e) => onBerthCallDragStart(e, berthCallItem, dragStartBerthCall)}
                                    onDragEnd={(e) => onDragEnd(e, berthCallItem)}
                                    onDragOver={(e) => onBerthCallDragOver(e, berthCallItem, dragStartBerthCall, draggoverElement, dragoverBerthCall)}
                                    onDrop={(e) => handleOnDrop(e, berthCallItem)}
                                >
                                    <ul>
                                        <StowageRotationTreeBerthCallItemComponent berthCall={berthCallItem} />
                                    </ul>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </li>
    );
};

export default StowageRotationTreePortCallItemComponent;
